import {
  Dialog,
  DialogActions,
  DialogContentText,
  DialogProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import styled from '@emotion/styled';
import { PmiDialogContent } from 'shell/components/shared/dialog/content';
import { useFormik } from 'formik';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { __AUTH_API_URL__, __ENVIRONMENT_TYPE__ } from 'shell/constants/environment';
import { Enviroment } from 'shell/types/environment';
import { LinkOtp } from 'shell/constants/link-otp';
import { AxiosResponse } from 'axios';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseOtp } from 'shell/models/response-otp';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const validationSchema = yup.object({
  otpCode: yup.string().required('Il campo è obbligatorio'),
});

const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

export interface OtpFormVM {
  otpCode?: string;
}

interface ModalOtpProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: OtpFormVM) => void;
  disabled?: boolean;
  otpCodeInitialValues?: string;
}

const ModalOtpComponent = ({
  open,
  onClose,
  onSave,
  disabled,
  otpCodeInitialValues = '',
}: ModalOtpProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const { userProfile, setUserProfile } = useUserProfile();

  const insertOTP = useHttpClient(``, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const otpForm = useFormik<OtpFormVM>({
    initialValues: {
      otpCode: otpCodeInitialValues,
    },
    validationSchema: validationSchema,
    onSubmit: (values: OtpFormVM) => {
      if (!values.otpCode) {
        return;
      }

      (
        insertOTP({
          url: `${__AUTH_API_URL__}/otp/${values.otpCode}`,
        }) as ResponsePromise<HttpResponse<ResponseOtp>, ResponseOtp>
      ).then((response) => {
        const castedResponse = response as AxiosResponse<HttpResponse<ResponseOtp>>;

        setUserProfile({
          ...(userProfile.datiLogin || {}),
          otp: castedResponse.data.payload.otp || '',
          currentTimestamp: castedResponse.data.payload.currentTimestamp || '',
        });
      });
      onSave(values);
    },
  });

  const linkRecoveryOtp: string | undefined = useMemo(() => {
    if (__ENVIRONMENT_TYPE__ === Enviroment.Development && LinkOtp.Development) {
      return LinkOtp.Development;
    }

    if (__ENVIRONMENT_TYPE__ === Enviroment.Staging && LinkOtp.Staging) {
      return LinkOtp.Staging;
    }

    if (LinkOtp.Production) {
      return LinkOtp.Production;
    }
  }, [__ENVIRONMENT_TYPE__]);

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        OTP
      </PmiDialogTitle>
      <PmiDialogContent style={{ padding: '30px 24px' }}>
        <DialogContentText id="alert-dialog-content">
          <PmiTextField
            label="Codice OTP *"
            aria-label="Codice OTP"
            id="otpCode"
            name="otpCode"
            fullWidth
            value={otpForm.values.otpCode}
            onChange={otpForm.handleChange}
            error={otpForm.touched.otpCode && Boolean(otpForm.errors.otpCode)}
            helperText={otpForm.errors.otpCode}
            disabled={disabled}
          />
        </DialogContentText>
      </PmiDialogContent>
      <DialogActions style={{ justifyContent: linkRecoveryOtp ? 'space-around' : '' }}>
        {linkRecoveryOtp ? (
          <a href={linkRecoveryOtp} target="_blank" rel="noreferrer">
            <PmiButton variant="outlined" color="cdmBrandThird">
              Recupera OTP
            </PmiButton>
          </a>
        ) : undefined}
        <PmiButton
          variant="outlined"
          color="cdmBrandThird"
          disabled={disabled || otpCodeInitialValues === otpForm.values.otpCode}
          onClick={() => otpForm.handleSubmit()}
        >
          Salva
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalOtpComponent.displayName = 'Modal Otp component';

const ModalOtp = memo(ModalOtpComponent);

export default ModalOtp;
